import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Table } from 'react-bootstrap';
import { inject, observer } from 'mobx-react';
import { confirmAlert } from 'react-confirm-alert';
import moment from 'moment';

//components
import withLocalization from '../../../../hoc/withLocalization';
import Button from '../../../../components/CustomButton/CustomButton.jsx';
import LoadingSpinner from '../../../../elements/LoadingSpinner';

//assets
import CloseModalIcon from '../../../../assets/img/CloseApproveModalIcon.svg';

const DEFAULT_DATE_FORMAT = 'DD.MM.YYYY';

const STATUS_LABELS = {
    active: 'Active',
    partially_reverted: 'Partially Reverted',
    reverted: 'Reverted',
};

const HoldProjectRegisterModal = ({ show, onHide, t, dateFormat, commonStore, vacationStore }) => {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(false);

    const loadEntries = useCallback(() => {
        setLoading(true);
        return vacationStore.getHoldProjectList()
            .then(rows => {
                setEntries(rows);
                setLoading(false);
            })
            .catch(err => {
                setLoading(false);
                commonStore.addNotification(err?.response?.body?.error || t('Something went wrong'), null, 'error');
            });
    }, [commonStore, vacationStore, t]);

    useEffect(() => {
        if (show) {
            loadEntries();
        }
    }, [show]);

    const doRevert = (id) => {
        return vacationStore.revertHoldProject(id)
            .then(res => {
                if (res.already_reverted) {
                    return commonStore.addNotification(t('This hold has already been reverted'), null, 'warning');
                }

                let message = `${t('Project hold reverted')}. ${res.reverted_user_ids.length} ${t('affected assignment(s) restored to their original schedule')}.`;
                if (res.blocked.length) {
                    message += ` ${res.blocked.length} ${t('could not be reverted because a later hold has since changed their roster')}.`;
                }
                commonStore.addNotification(message, null, res.blocked.length ? 'warning' : 'success');
                loadEntries();
            })
            .catch(err => {
                commonStore.addNotification(err?.response?.body?.error || t('Something went wrong'), null, 'error');
            });
    };

    const handleRevert = (entry) => {
        confirmAlert({
            title: t('Confirm'),
            message: (
                <div style={{ fontFamily: '"Exo 2", sans-serif' }}>
                    <p>{t('Revert this hold for')} {entry.member_names.join(', ')}? {t('Affected assignments will be moved back to their original schedule.')}</p> <b className='text-danger'>{t('Changes will be lost that are made after the stagger.')}</b>
                </div>
            ),
            buttons: [
                { label: t('Yes'), onClick: () => doRevert(entry.id) },
                { label: t('Cancel'), onClick: () => { } },
            ],
        });
    };

    if (!show) return null;
    const format = dateFormat || DEFAULT_DATE_FORMAT;

    return (
        <Modal centered size="lg" show={show} onHide={onHide} className="hold-project-modal">
            <Modal.Header>
                <Modal.Title className="fs14 fw-700">{t('Paused / Hold Projects')}</Modal.Title>
                <img src={CloseModalIcon} className="cursor-pointer" alt="close icon" onClick={onHide} />
            </Modal.Header>
            <Modal.Body>
                {loading ? <LoadingSpinner /> : (
                    <div className="table-scroll">
                        <Table className="table-sm fs12 table-container">
                            <thead>
                                <tr>
                                    <th>{t('Members')}</th>
                                    <th>{t('From')}</th>
                                    <th>{t('To')}</th>
                                    <th>{t('Total days')}</th>
                                    <th>{t('Status')}</th>
                                    <th>{t('Registered By')}</th>
                                    <th>{t('Actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {entries.map(entry => (
                                    <tr key={entry.id}>
                                        <td className="fw-600">{entry.member_names.join(', ')}</td>
                                        <td>{moment(entry.from_date).format(format)}</td>
                                        <td>{moment(entry.to_date).format(format)}</td>
                                        <td>{entry.total_days}</td>
                                        <td>
                                            <span className={`hold-project-register-status ${entry.status}`}>
                                                {t(STATUS_LABELS[entry.status] || entry.status)}
                                            </span>
                                        </td>
                                        <td>{entry.registered_by}</td>
                                        <td>
                                            {entry.status !== 'reverted'
                                                ? <Button wd onClick={() => handleRevert(entry)}>{t('Revert')}</Button>
                                                : <span className="text-muted fs12 fst-italic">{t('Reverted')} {entry.reverted_on ? moment(entry.reverted_on).format(format) : ''}</span>}
                                        </td>
                                    </tr>
                                ))}
                                {!entries.length && (
                                    <tr><td colSpan={7} className="text-center text-muted py-3">{t('No paused/hold projects registered yet')}</td></tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button onClick={onHide}>{t('Close')}</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default inject('commonStore', 'vacationStore')(withLocalization(observer(HoldProjectRegisterModal)));
